import type {
  LayoutSettingChange,
  LayoutSettingControl,
} from "~/v3-poc/features/workspace/shared/layoutSettings/layoutSettings.types";
import { parseNumberDraft } from "~/v3-poc/features/workspace/shared/layoutSettings/numberDraft";
import {
  REACT_GRID_LAYOUT_SCHEMA,
  type RglSettingId,
} from "./reactGridLayoutSettings.schema";
import {
  DEFAULT_REACT_GRID_LAYOUT_SETTINGS,
  type ReactGridLayoutSettings,
} from "./reactGridLayoutSettings.types";
import * as inv from "./rglSettings.invariants";
import * as wr from "./rglSettings.writers";

export interface RglSettingsState {
  config: ReactGridLayoutSettings;
  drafts: Partial<Record<RglSettingId, string>>;
  errors: Partial<Record<RglSettingId, string>>;
  expanded: boolean;
}

export type RglSettingsAction =
  | { type: "toggle-expanded" }
  | { type: "reset" }
  | { type: "hydrate"; config: ReactGridLayoutSettings }
  | { type: "field-change"; fieldId: RglSettingId; change: LayoutSettingChange }
  | { type: "commit-draft"; fieldId: RglSettingId; normalize: boolean };

export const INITIAL_RGL_SETTINGS_STATE: RglSettingsState = {
  config: DEFAULT_REACT_GRID_LAYOUT_SETTINGS,
  drafts: {},
  errors: {},
  expanded: false,
};

export const createInitialRglSettingsState = (
  initialConfig?: ReactGridLayoutSettings,
): RglSettingsState =>
  initialConfig
    ? { ...INITIAL_RGL_SETTINGS_STATE, config: initialConfig }
    : INITIAL_RGL_SETTINGS_STATE;

type ErrorMap = Partial<Record<RglSettingId, string>>;
type DraftMap = Partial<Record<RglSettingId, string>>;

const clearError = (errors: ErrorMap, id: RglSettingId): ErrorMap => {
  if (errors[id] === undefined) {
    return errors;
  }
  const next = { ...errors };
  delete next[id];
  return next;
};

const setError = (
  errors: ErrorMap,
  id: RglSettingId,
  message: string,
): ErrorMap => ({ ...errors, [id]: message });

const clearDraft = (drafts: DraftMap, id: RglSettingId): DraftMap => {
  if (drafts[id] === undefined) {
    return drafts;
  }
  const next = { ...drafts };
  delete next[id];
  return next;
};

export const rglSettingsReducer = (
  state: RglSettingsState,
  action: RglSettingsAction,
): RglSettingsState => {
  switch (action.type) {
    case "toggle-expanded":
      return { ...state, expanded: !state.expanded };
    case "reset":
      return INITIAL_RGL_SETTINGS_STATE;
    case "hydrate":
      return {
        ...INITIAL_RGL_SETTINGS_STATE,
        config: action.config,
        expanded: state.expanded,
      };
    case "field-change":
      return applyFieldChange(state, action.fieldId, action.change);
    case "commit-draft":
      return commitDraft(state, action.fieldId, action.normalize);
  }
};

const commitDraft = (
  state: RglSettingsState,
  fieldId: RglSettingId,
  normalize: boolean,
): RglSettingsState => {
  const descriptor = REACT_GRID_LAYOUT_SCHEMA[fieldId];
  if (!descriptor || descriptor.status === "unavailable") {
    return state;
  }
  const control = descriptor.control;
  if (control.kind !== "number") {
    return state;
  }
  if (inv.disabledForRglSetting(state.config, fieldId).disabled) {
    return state;
  }
  const draft = state.drafts[fieldId];
  if (draft === undefined) {
    return state;
  }
  const parsed = parseNumberDraft(draft, {
    min: control.min,
    max: control.max,
    integer: control.integer,
  });
  if (!parsed.ok) {
    // Invalid text stays visible with its error and never reaches the engine.
    return state.errors[fieldId] === parsed.error
      ? state
      : { ...state, errors: setError(state.errors, fieldId, parsed.error) };
  }
  const nextConfig = wr.writeRglNumeric(state.config, fieldId, parsed.value);
  const crossError = inv.numericRglCrossFieldError(nextConfig, fieldId);
  if (crossError) {
    return state.errors[fieldId] === crossError
      ? state
      : { ...state, errors: setError(state.errors, fieldId, crossError) };
  }
  const drafts = normalize ? clearDraft(state.drafts, fieldId) : state.drafts;
  const configChanged =
    wr.readRglNumeric(state.config, fieldId) !== parsed.value;
  if (!configChanged && drafts === state.drafts) {
    return clearError(state.errors, fieldId) === state.errors
      ? state
      : { ...state, errors: clearError(state.errors, fieldId) };
  }
  return {
    ...state,
    config: configChanged ? nextConfig : state.config,
    drafts,
    errors: clearError(state.errors, fieldId),
  };
};

const applyDraftKeystroke = (
  state: RglSettingsState,
  fieldId: RglSettingId,
  control: Extract<LayoutSettingControl, { kind: "number" }>,
  raw: string,
): RglSettingsState => {
  const drafts = { ...state.drafts, [fieldId]: raw };
  const parsed = parseNumberDraft(raw, {
    min: control.min,
    max: control.max,
    integer: control.integer,
  });
  if (!parsed.ok) {
    return {
      ...state,
      drafts,
      errors: setError(state.errors, fieldId, parsed.error),
    };
  }
  const crossError = inv.numericRglCrossFieldError(
    wr.writeRglNumeric(state.config, fieldId, parsed.value),
    fieldId,
  );
  return {
    ...state,
    drafts,
    errors: crossError
      ? setError(state.errors, fieldId, crossError)
      : clearError(state.errors, fieldId),
  };
};

const applyFieldChange = (
  state: RglSettingsState,
  fieldId: RglSettingId,
  change: LayoutSettingChange,
): RglSettingsState => {
  const descriptor = REACT_GRID_LAYOUT_SCHEMA[fieldId];
  if (!descriptor) {
    return state;
  }
  if (descriptor.status === "unavailable") {
    return state;
  }
  if (inv.disabledForRglSetting(state.config, fieldId).disabled) {
    return state;
  }
  const control: LayoutSettingControl = descriptor.control;

  if (change.kind === "boolean" && control.kind === "boolean") {
    const nextConfig = wr.writeRglBoolean(state.config, fieldId, change.value);
    if (
      change.value === true &&
      (fieldId === "globalMinEnabled" || fieldId === "globalMaxEnabled")
    ) {
      const err = inv.rglEnableToggleInvariantError(nextConfig);
      if (err) {
        return { ...state, errors: setError(state.errors, fieldId, err) };
      }
    }
    // Clears only this boolean's own error; dependents keep stale draft/error.
    return {
      ...state,
      config: nextConfig,
      errors: clearError(state.errors, fieldId),
    };
  }

  if (change.kind === "select" && control.kind === "select") {
    return {
      ...state,
      config: wr.writeRglSelect(state.config, fieldId, change.value),
    };
  }

  if (change.kind === "draft" && control.kind === "number") {
    return applyDraftKeystroke(state, fieldId, control, change.value);
  }

  if (change.kind === "handle" && control.kind === "handles") {
    if (!wr.isRglResizeHandle(change.value)) {
      return state;
    }
    const next = wr.writeRglResizeHandle(
      state.config.resizeHandles,
      change.value,
      change.checked,
    );
    const emptyError = inv.rglResizeHandlesInvariantError(next);
    if (emptyError) {
      return { ...state, errors: setError(state.errors, fieldId, emptyError) };
    }
    return {
      ...state,
      config: { ...state.config, resizeHandles: next },
      errors: clearError(state.errors, fieldId),
    };
  }

  return state;
};
